import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api';

export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <header><h2 className="grow">{title}</h2><button className="ghost" onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button></header>
        <section>{children}</section>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

/* ---------- icon set: 24px grid, 1.8 stroke, currentColor. One place, used everywhere. ---------- */
const ICONS = {
  dashboard: 'M4 13h6V4H4zM14 20h6V4h-6zM4 20h6v-3H4z',
  today: 'M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  register: 'M6 3h12v5H6zM4 8h16v12H4zM8 12h2M12 12h2M16 12h0M8 16h2M12 16h2M16 16h0',
  cash: 'M3 7h18v10H3zM12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM6 10h0M18 14h0',
  card: 'M3 6h18v12H3zM3 10h18M7 15h4',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2',
  phone: 'M8 2h8a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM11 18h2',
  ticket: 'M3 6h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4zM9 6v12',
  cart: 'M3 4h2l2.4 11h10.2L20 7H6.2M9 20h0M17 20h0',
  doc: 'M7 3h8l4 4v14H7zM15 3v4h4M10 12h6M10 16h6',
  receipt: 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  truck: 'M2 6h11v10H2zM13 9h5l3 3v4h-8M6 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM17 19a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20c.6-3.4 3-5 6.5-5s5.9 1.6 6.5 5M16 4.3a3.5 3.5 0 0 1 0 6.4M18 15c2 .5 3.2 2 3.5 5',
  bottle: 'M10 2h4v4l1.5 3v11a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2V9L10 6zM8.5 13h7',
  layers: 'M12 3 3 8l9 5 9-5zM3 13l9 5 9-5',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 8h0',
  swap: 'M4 8h14l-3-3M20 16H6l3 3',
  clipboard: 'M9 4h6v3H9zM6 5h3M15 5h3v16H6V5M9 12h6M9 16h4',
  star: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z',
  gift: 'M4 9h16v4H4zM5 13h14v8H5zM12 9v12M12 9c-2 0-4-1-4-3s3-2 4 3zM12 9c2 0 4-1 4-3s-3-2-4 3z',
  percent: 'M19 5 5 19M7 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  box: 'M3 7l9-4 9 4v10l-9 4-9-4zM3 7l9 4 9-4M12 11v10',
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3M9 21h6',
  send: 'M4 12l16-8-6 16-2.5-6.5z',
  sparkle: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  trend: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  coins: 'M12 4c4.4 0 8 1.1 8 2.5S16.4 9 12 9 4 7.9 4 6.5 7.6 4 12 4zM4 6.5v5C4 12.9 7.6 14 12 14s8-1.1 8-2.5v-5M4 11.5v5C4 17.9 7.6 19 12 19s8-1.1 8-2.5v-5',
  money: 'M12 2v20M17 6.5C16 5 14.2 4.5 12 4.5c-2.8 0-4.5 1.2-4.5 3s1.7 2.6 4.5 3 4.5 1.2 4.5 3-1.8 3-4.5 3c-2.2 0-4-.6-5-2',
  plug: 'M9 2v5M15 2v5M6 7h12v4a6 6 0 0 1-12 0zM12 17v5',
  gear: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  x: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4-4',
  refresh: 'M20 11a8 8 0 0 0-14-4M4 4v4h4M4 13a8 8 0 0 0 14 4M20 20v-4h-4',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6',
  print: 'M7 9V3h10v6M7 17H4v-7h16v7h-3M7 14h10v7H7z',
  logout: 'M10 4H5v16h5M15 8l4 4-4 4M19 12H9',
  hold: 'M8 5v14M16 5v14',
  recall: 'M4 12a8 8 0 1 0 3-6.2M4 4v4h4',
  split: 'M12 3v6M12 9 5 20M12 9l7 11',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 6l6 6-6 6',
  store: 'M4 9l1.5-5h13L20 9M4 9v11h16V9M10 20v-5h4v5',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13 7l4 4',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h0M4 12h0M4 18h0',
  open: 'M14 4h6v6M20 4l-9 9M18 14v6H4V6h6',
  sliders: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M16 4v4M10 10v4M18 16v4',
} as const;
export type IconName = keyof typeof ICONS;
export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg className={`ico${className ? ` ${className}` : ''}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d={ICONS[name]} /></svg>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof ApiError || error instanceof Error ? error.message : String(error);
  return <div className="notice bad" role="alert">{msg}</div>;
}

/** Load data with loading/error state and a reload function. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; error: unknown; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    fn().then((d) => { if (live) { setData(d); setError(null); } }).catch((e) => { if (live) setError(e); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { data, error, loading, reload: () => setN((x) => x + 1) };
}

const FLAG_TEXT: Record<string, [string, 'bad' | 'warn' | 'good' | '']> = {
  UNMATCHED: ['Not matched', 'bad'],
  NEW_TO_STORE: ['New to store', 'warn'],
  LOW_CONFIDENCE: ['Check reading', 'warn'],
  MATH_MISMATCH: ['Qty × price ≠ total', 'bad'],
  PRICE_ABOVE_CPL: ['Above CPL price', 'bad'],
  PRICE_BELOW_CPL: ['Below CPL price', 'good'],
  NOT_ON_CPL: ['Not on CPL', 'warn'],
  NOT_ON_PO: ['Not on PO', 'warn'],
  SHORT_SHIP: ['Short shipped', 'bad'],
  OVER_SHIP: ['Over shipped', 'warn'],
  NON_STOCK: ['Charge, not stock', ''],
  DUPLICATE_INVOICE_NO: ['Duplicate invoice number', 'bad'],
  TOTAL_MISMATCH: ['Lines ≠ invoice total', 'warn'],
  NO_WHOLESALER: ['Pick the vendor', 'bad'],
};
export function Flag({ code }: { code: string }) {
  const [text, tone] = FLAG_TEXT[code] ?? [code, ''];
  return <span className={`chip ${tone}`}>{text}</span>;
}
